if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const mainVariantId = String(this.form.querySelector('[name="id"]').value || '').trim();
        const sellingPlanInput = this.form.querySelector('[name="selling_plan"]');
        const sellingPlanId = sellingPlanInput && sellingPlanInput.value ? sellingPlanInput.value : null;
        const isBundleEnabled = this.dataset.welcomeKitEnabled === 'true';

        let giftVariantIds = [];
        if (isBundleEnabled && sellingPlanId) {
          const multiBoxSoupVariantIds = new Set([
            '44567358505017',
            '42790261719097',
            '44634242940985',
            '44634242973753',
          ]);
          let boxQty = parseInt(this.dataset.selectedBoxQty || this.form.dataset.boxQty || '0', 10);
          if (!boxQty || Number.isNaN(boxQty)) {
            boxQty = multiBoxSoupVariantIds.has(mainVariantId) ? 2 : 1;
          } else if (boxQty === 1 && multiBoxSoupVariantIds.has(mainVariantId)) {
            boxQty = 2;
          }

          const rawGiftIds =
            boxQty === 1
              ? this.getAttribute('data-welcome-kit-1-box') || this.dataset.welcomeKit1Box
              : this.getAttribute('data-welcome-kit-multi-box') || this.dataset.welcomeKitMultiBox;
          if (rawGiftIds) {
            giftVariantIds = rawGiftIds
              .split(',')
              .map((id) => id.trim())
              .filter((id) => id.length > 0);
          }
        }

        const allConfiguredGiftIds = new Set(
          [
            this.getAttribute('data-welcome-kit-1-box') || this.dataset.welcomeKit1Box || '',
            this.getAttribute('data-welcome-kit-multi-box') || this.dataset.welcomeKitMultiBox || '',
          ]
            .join(',')
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean),
        );

        const performAddToCart = (liveCartItems) => {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';

          // Combine DOM and live /cart.js items so back-button (bfcache) or multi-tab carts never double gifts
          const existingGiftIds = new Set(
            [...document.querySelectorAll('.cart-item[data-is-free-gift="true"]')].map((el) =>
              String(el.dataset.variantId || '').trim(),
            ),
          );
          if (Array.isArray(liveCartItems)) {
            liveCartItems.forEach((item) => {
              const vId = String(item.variant_id || item.id || '').trim();
              if (item.final_line_price === 0 || allConfiguredGiftIds.has(vId)) {
                existingGiftIds.add(vId);
              }
            });
          }

          const giftsToAdd = giftVariantIds.filter((id) => !existingGiftIds.has(id));

          if (giftsToAdd.length > 0) {
            config.headers['Content-Type'] = 'application/json';

            const mainQty = parseInt(this.form.querySelector('[name="quantity"]')?.value || '1', 10);
            const items = [
              {
                id: parseInt(mainVariantId, 10),
                quantity: mainQty,
                selling_plan: parseInt(sellingPlanId, 10),
              },
            ];

            giftsToAdd.forEach((giftId) => {
              items.push({
                id: parseInt(giftId, 10),
                quantity: 1,
                properties: {
                  _welcome_kit_item: 'true',
                },
              });
            });

            // Shopify's /cart/add.js prepends each item into cart.items (LIFO).
            // On real Shopify domains, Checkout renders cart.items in index order (0..N),
            // so reversing the array places the main product first, followed by free gifts in order.
            // On the local CLI proxy (127.0.0.1 / localhost), the proxy reverses cart.items a second
            // time when creating the remote checkout, so we keep the natural order there.
            const isLocalProxy =
              window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost';
            if (!isLocalProxy) {
              items.reverse();
            }

            const payload = {
              items: items,
            };

            if (this.cart) {
              payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
              payload.sections_url = window.location.pathname;
              this.cart.setActiveElement(document.activeElement);
            }

            config.body = JSON.stringify(payload);
          } else {
            delete config.headers['Content-Type'];
            const formData = new FormData(this.form);
            if (this.cart) {
              formData.append(
                'sections',
                this.cart.getSectionsToRender().map((section) => section.id),
              );
              formData.append('sections_url', window.location.pathname);
              this.cart.setActiveElement(document.activeElement);
            }
            config.body = formData;
          }

          return fetch(`${routes.cart_add_url}`, config)
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: mainVariantId,
                  errors: response.errors || response.description,
                  message: response.message,
                });
                this.handleErrorMessage(response.description);

                const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                if (!soldOutMessage) return;
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButton.querySelector('span').classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
                return;
              } else if (!this.cart) {
                window.location = window.routes.cart_url;
                return;
              }

              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: mainVariantId,
                  cartData: response,
                });
              this.error = false;
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      this.cart.renderContents(response);
                    });
                  },
                  { once: true },
                );
                quickAddModal.hide(true);
              } else {
                this.cart.renderContents(response);
              }
            });
        };

        const cartCheckPromise =
          giftVariantIds.length > 0
            ? fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
                credentials: 'same-origin',
                cache: 'no-store',
              })
                .then((res) => (res.ok ? res.json() : null))
                .then((cartData) => (cartData && Array.isArray(cartData.items) ? cartData.items : null))
                .catch(() => null)
            : Promise.resolve(null);

        cartCheckPromise
          .then((liveCartItems) => performAddToCart(liveCartItems))
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );
}
